import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
const DATA_MANAGER_TABLE_SCROLL_STORAGE_KEY = "grain-saas:data-manager:table-scroll:v1";

const memoryTableScrollSnapshot = {};

function getWindowSessionStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return userUiPreferenceStorage("session");
  } catch {
    return null;
  }
}

function resolveTableScrollStorage(storage = null) {
  if (
    storage
    && typeof storage.getItem === "function"
    && typeof storage.setItem === "function"
    && typeof storage.removeItem === "function"
  ) {
    return storage;
  }

  return getWindowSessionStorage();
}

function parseStorageSnapshot(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(value);
    return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
      ? parsedValue
      : null;
  } catch {
    return null;
  }
}

function normalizeScrollOffset(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0
    ? Math.round(numericValue)
    : 0;
}

export function normalizeDataManagerTableScrollState(scrollState = null) {
  return {
    scrollLeft: normalizeScrollOffset(scrollState?.scrollLeft),
    scrollTop: normalizeScrollOffset(scrollState?.scrollTop),
  };
}

function readTableScrollSnapshot(storage = null) {
  const resolvedStorage = resolveTableScrollStorage(storage);

  if (resolvedStorage) {
    try {
      const rawSnapshot = resolvedStorage.getItem(DATA_MANAGER_TABLE_SCROLL_STORAGE_KEY);
      const parsedSnapshot = parseStorageSnapshot(rawSnapshot);
      if (parsedSnapshot) {
        return parsedSnapshot;
      }
    } catch {
      return { ...memoryTableScrollSnapshot };
    }
  }

  return { ...memoryTableScrollSnapshot };
}

function writeTableScrollSnapshot(snapshot, storage = null) {
  const resolvedStorage = resolveTableScrollStorage(storage);
  Object.keys(memoryTableScrollSnapshot).forEach((tableKey) => {
    delete memoryTableScrollSnapshot[tableKey];
  });
  Object.assign(memoryTableScrollSnapshot, snapshot);

  if (!resolvedStorage) {
    return;
  }

  try {
    resolvedStorage.setItem(DATA_MANAGER_TABLE_SCROLL_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage is best-effort only.
  }
}

export function readDataManagerTableScrollState(tableKey, storage = null) {
  const normalizedTableKey = typeof tableKey === "string" ? tableKey.trim() : "";
  if (!normalizedTableKey) {
    return null;
  }

  const snapshot = readTableScrollSnapshot(storage);
  const persistedScrollState = snapshot[normalizedTableKey];
  return persistedScrollState ? normalizeDataManagerTableScrollState(persistedScrollState) : null;
}

export function persistDataManagerTableScrollState(tableKey, scrollState, storage = null) {
  const normalizedTableKey = typeof tableKey === "string" ? tableKey.trim() : "";
  if (!normalizedTableKey) {
    return normalizeDataManagerTableScrollState(scrollState);
  }

  const normalizedScrollState = normalizeDataManagerTableScrollState(scrollState);
  const nextSnapshot = {
    ...readTableScrollSnapshot(storage),
    [normalizedTableKey]: normalizedScrollState,
  };
  writeTableScrollSnapshot(nextSnapshot, storage);
  return normalizedScrollState;
}

export function clearDataManagerTableScrollState(tableKey = "", storage = null) {
  const normalizedTableKey = typeof tableKey === "string" ? tableKey.trim() : "";
  if (!normalizedTableKey) {
    return;
  }

  const nextSnapshot = readTableScrollSnapshot(storage);
  delete nextSnapshot[normalizedTableKey];
  writeTableScrollSnapshot(nextSnapshot, storage);
}

export { DATA_MANAGER_TABLE_SCROLL_STORAGE_KEY };
